/**
 * reviews.ts - SNS review templates for visitor feedback
 */

/**
 * Review templates organized by star rating (1-5).
 * Each template has an i18n key. Some templates include {ghost} placeholder
 * which gets replaced with a ghost name.
 */
export interface ReviewTemplate {
  stars: 1 | 2 | 3 | 4 | 5;
  textKey: string;
  requiresGhost: boolean; // if true, {ghost} param will be injected
}

export const REVIEW_TEMPLATES: ReviewTemplate[] = [
  // 5 stars
  { stars: 5, textKey: 'review_5_1', requiresGhost: true },
  { stars: 5, textKey: 'review_5_2', requiresGhost: false },
  { stars: 5, textKey: 'review_5_3', requiresGhost: true },
  { stars: 5, textKey: 'review_5_4', requiresGhost: false },
  // 4 stars
  { stars: 4, textKey: 'review_4_1', requiresGhost: true },
  { stars: 4, textKey: 'review_4_2', requiresGhost: false },
  { stars: 4, textKey: 'review_4_3', requiresGhost: true },
  { stars: 4, textKey: 'review_4_4', requiresGhost: false },
  // 3 stars
  { stars: 3, textKey: 'review_3_1', requiresGhost: false },
  { stars: 3, textKey: 'review_3_2', requiresGhost: true },
  { stars: 3, textKey: 'review_3_3', requiresGhost: false },
  // 2 stars
  { stars: 2, textKey: 'review_2_1', requiresGhost: true },
  { stars: 2, textKey: 'review_2_2', requiresGhost: false },
  { stars: 2, textKey: 'review_2_3', requiresGhost: true },
  // 1 star
  { stars: 1, textKey: 'review_1_1', requiresGhost: false },
  { stars: 1, textKey: 'review_1_2', requiresGhost: false },
  { stars: 1, textKey: 'review_1_3', requiresGhost: true },
];

/**
 * Given a scare score (total SP earned from the group), determine star rating.
 */
export function calcReviewStars(totalSpEarned: number, numRooms: number): 1 | 2 | 3 | 4 | 5 {
  if (numRooms === 0) return 1;
  const avgPerRoom = totalSpEarned / numRooms;
  if (avgPerRoom >= 40) return 5;
  if (avgPerRoom >= 25) return 4;
  if (avgPerRoom >= 15) return 3;
  if (avgPerRoom >= 5) return 2;
  return 1;
}

/**
 * Pick a random review template matching the given star rating.
 */
export function pickReviewTemplate(stars: 1 | 2 | 3 | 4 | 5): ReviewTemplate {
  const matching = REVIEW_TEMPLATES.filter((r) => r.stars === stars);
  const idx = Math.floor(Math.random() * matching.length);
  return matching[idx] ?? REVIEW_TEMPLATES[0];
}

/**
 * Fame earned from a review.
 */
export function fameFromReview(stars: 1 | 2 | 3 | 4 | 5): number {
  const fameMap: Record<number, number> = { 1: -2, 2: 0, 3: 1, 4: 3, 5: 5 };
  return fameMap[stars] ?? 0;
}
